import DownloadIcon from '@mui/icons-material/Download';
import {
  Alert,
  Box,
  Button,
  Link as MuiLink,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { GENERATED_FILE_REPORTS, getReportDef, liveColumns } from '@app/shared';
import type {
  GeneratedReportFileDto,
  ReportColumn,
  ReportDef,
  ReportResult,
  ReportRow,
  ReportValue,
} from '@app/shared';
import type { AgentChain } from '../../components/AgentChainSelect';
import { ColumnChooser, useColumnChoice } from '../../components/ColumnChooser';
import { DataTable, rowNumber } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { PageHeader } from '../../components/PageHeader';
import { ApiError, api } from '../../lib/api';
import type { Query } from '../../lib/api';
import { formatCents, formatDate, formatDateTime } from '../../lib/format';
import { ReleasedFeature, useFeatureReleased } from '../../release';
import { RequirePermission } from '../auth/guards';
import { ReportFilterFields, filtersToQuery } from './FilterFields';
import type { FilterDraft } from './FilterFields';

/** How a report value reads on screen. */
function show(kind: ReportColumn['kind'], value: ReportValue | undefined): string {
  if (value === null || value === undefined || value === '') return '';
  if (kind === 'money' && typeof value === 'number') return formatCents(value);
  if (kind === 'datetime' && typeof value === 'string') return formatDateTime(value);
  if (kind === 'date' && typeof value === 'string') return formatDate(value);
  if (kind === 'yesno' || typeof value === 'boolean') return value ? 'Y' : 'N';
  return String(value);
}

/** A calendar day (YYYY-MM-DD) as the lists show dates, with no time-zone shift. */
const day = (d: string | null) => (d ? `${d.slice(5, 7)}/${d.slice(8, 10)}/${d.slice(0, 4)}` : '');

/**
 * "Date Period | Generated Date | Download Link" (switch `report-generated-files`): the files the
 * user generated from this report by downloading it.
 */
function GeneratedFiles({ reportKey }: { reportKey: string }) {
  const files = useQuery({
    queryKey: ['reports', reportKey, 'files'],
    queryFn: () => api.get<{ data: GeneratedReportFileDto[] }>(`/reports/${reportKey}/files`),
    select: (r) => r.data,
  });
  return (
    <Paper variant="outlined" sx={{ mb: 1.5 }} component="section" aria-label="Generated files">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell>
            <TableCell>Date Period</TableCell>
            <TableCell>Generated Date</TableCell>
            <TableCell>Download Link</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(files.data ?? []).map((f, i) => (
            <TableRow key={f.id}>
              <TableCell>{i + 1}</TableCell>
              <TableCell>
                {f.dateFrom || f.dateTo ? `${day(f.dateFrom)} - ${day(f.dateTo)}` : 'All dates'}
              </TableCell>
              <TableCell>{formatDateTime(f.generatedAt)}</TableCell>
              <TableCell>
                <MuiLink href={api.url(`/reports/${reportKey}/files/${f.id}`)} download>
                  Download
                </MuiLink>
              </TableCell>
            </TableRow>
          ))}
          {files.data && files.data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4}>No file generated yet. Download CSV generates one.</TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </Paper>
  );
}

export interface ReportScreenProps {
  def: ReportDef;
  /** Buttons beside the heading, before "Download CSV". */
  headerActions?: ReactNode;
  /** Content of an "Action" column; a row has an `id` when its screen offers actions. */
  rowActions?: (row: ReportRow) => ReactNode;
  /** Header of that column. */
  actionsHeader?: string;
  /** Shown between the filters and the result. */
  children?: ReactNode;
  /** Shown under the heading, above the filters (tabs of a page with several lists). */
  top?: ReactNode;
  /** More columns after the screen's own, each with its own header (per-row buttons). */
  extraColumns?: Column<ReportRow>[];
  /** Gives the screen a "Columns" button; this is the title of its dialog on the reference. */
  chooserTitle?: string;
}

/** React Query key prefix of a screen's rows, for invalidating after an action. */
export const reportQueryKey = (key: string) => ['reports', key] as const;

export function ReportScreen({
  def,
  headerActions,
  rowActions,
  actionsHeader = 'Action',
  children,
  top,
  extraColumns = [],
  chooserTitle,
}: ReportScreenProps) {
  const choice = useColumnChoice(`screen.${def.key}`, def.columns);
  const featureOn = useFeatureReleased();
  const queryClient = useQueryClient();
  const keepsFiles =
    GENERATED_FILE_REPORTS.includes(def.key) && featureOn('report-generated-files');
  const reset =
    def.reset && (!def.reset.feature || featureOn(def.reset.feature)) ? def.reset : null;
  const [draft, setDraft] = useState<FilterDraft>({});
  const [chain, setChain] = useState<AgentChain>({});
  const [applied, setApplied] = useState<Query | null>(def.autoRun ? {} : null);
  const [problems, setProblems] = useState<Record<string, string>>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const result = useQuery({
    queryKey: ['reports', def.key, applied, page, pageSize],
    queryFn: () => api.get<ReportResult>(`/reports/${def.key}`, { ...applied, page, pageSize }),
    enabled: applied !== null,
    placeholderData: (prev) => prev,
  });

  const set = (key: string, value: string) => {
    setDraft((d) => ({ ...d, [key]: value }));
    setProblems((p) => ({ ...p, [key]: '' }));
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const { query, problems: found } = filtersToQuery(def, draft, chain);
    setProblems(found);
    if (Object.keys(found).length) return;
    setApplied(query);
    setPage(1);
  };

  // "Reset Search": empty filters, and the report runs again without them.
  const clear = () => {
    setDraft({});
    setChain({});
    setProblems({});
    setApplied(filtersToQuery(def, {}, {}).query);
    setPage(1);
  };

  // Field errors the server reports (a date typed by hand, a user out of reach).
  const serverProblems: Record<string, string> = {};
  if (result.error instanceof ApiError)
    for (const d of result.error.details) serverProblems[d.path] = d.message;
  const problem = (key: string) => problems[key] || serverProblems[key] || '';

  const columns: Column<ReportRow>[] = [
    { key: '#', header: '#', width: 50, render: (_r, i) => rowNumber(i, page, pageSize) },
    ...liveColumns(def.columns, featureOn)
      .filter((c) => !chooserTitle || choice.shown.has(c.key))
      .map((c): Column<ReportRow> => ({
        key: c.key,
        header: c.header,
        align: c.kind === 'money' || c.kind === 'number' ? 'right' : undefined,
        nowrap: c.kind === 'datetime' || c.kind === 'date',
        render: (row) => {
          const text = show(c.kind, row[c.key]);
          // Requests and responses are long; keep the table readable.
          return text.length > 80 ? (
            <Box sx={{ maxWidth: 380, fontSize: 12, wordBreak: 'break-all' }}>{text}</Box>
          ) : (
            text
          );
        },
      })),
    ...(rowActions
      ? [{ key: 'action', header: actionsHeader, render: (row: ReportRow) => rowActions(row) }]
      : []),
    ...extraColumns,
  ];

  return (
    <Box>
      <PageHeader
        title={def.heading}
        code={def.code}
        actions={
          <>
            {headerActions}
            {chooserTitle ? (
              <ColumnChooser
                title={chooserTitle}
                columns={def.columns}
                shown={choice.shown}
                onSave={choice.save}
              />
            ) : null}
            {applied !== null ? (
              <Button
                component="a"
                href={api.url(`/reports/${def.key}/export`, applied)}
                download
                onClick={() => {
                  // The download is kept as a file; read the list again once it is made.
                  if (keepsFiles)
                    setTimeout(
                      () =>
                        void queryClient.invalidateQueries({
                          queryKey: ['reports', def.key, 'files'],
                        }),
                      1500,
                    );
                }}
                variant="outlined"
                startIcon={<DownloadIcon />}
              >
                Download CSV
              </Button>
            ) : null}
          </>
        }
      />
      {top}
      {def.filters.length ? (
        <Paper
          variant="outlined"
          component="form"
          noValidate
          onSubmit={submit}
          aria-label={`${def.heading} filters`}
          sx={{
            p: 1.5,
            mb: 1.5,
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
            alignItems: 'start',
          }}
        >
          <ReportFilterFields
            def={def}
            draft={draft}
            onChange={set}
            chain={chain}
            onChainChange={setChain}
            problem={problem}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 37 }}>
            <Button type="submit" variant="contained">
              {def.submitLabel ?? 'Submit'}
            </Button>
            {reset ? <Button onClick={clear}>{reset.label}</Button> : null}
          </Box>
        </Paper>
      ) : null}

      {keepsFiles ? <GeneratedFiles reportKey={def.key} /> : null}
      {children}
      {applied === null ? null : (
        <>
          {def.resultHeading === null ? null : (
            <Typography variant="h3" sx={{ mb: 1 }}>
              {def.resultHeading ?? 'Search Results :'} {result.data?.total ?? ''}
            </Typography>
          )}
          <DataTable
            label={def.heading}
            columns={columns}
            rows={result.data?.data}
            rowKey={(row) => (typeof row.id === 'string' ? row.id : JSON.stringify(row))}
            loading={result.isFetching}
            // Field errors are shown on their fields.
            error={
              result.error instanceof ApiError && result.error.details.length
                ? undefined
                : result.error
            }
            onRetry={() => void result.refetch()}
            emptyText={def.emptyText}
            page={page}
            pageSize={pageSize}
            total={result.data?.total ?? 0}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            pageSizeOptions={[10, 20, 50, 100, 200]}
          />
        </>
      )}
    </Box>
  );
}

/**
 * One report or log of the engine. The key comes from the route (`/reports/:key`) or from
 * the `reportKey` prop for screens with their own menu entry (REST API Log, USPS API Log).
 */
export function ReportPage({ reportKey }: { reportKey?: string }) {
  const params = useParams();
  const def = getReportDef(reportKey ?? params.key ?? '');
  if (!def) return <Alert severity="warning">This report does not exist.</Alert>;
  const screen = (
    <RequirePermission code={def.code}>
      {/* `key` gives each report its own filters and paging. */}
      <ReportScreen key={def.key} def={def} />
    </RequirePermission>
  );
  return def.feature ? (
    <ReleasedFeature name={def.feature} page>
      {screen}
    </ReleasedFeature>
  ) : (
    screen
  );
}
